import React from 'react';
import { motion } from 'framer-motion';
import { getMasteryLabelInfo } from '../../learningLab/shared/masteryLabelInfo';
interface MasteryRingProps {
  /** Mastery value 0-100. Use this for preset-color mode. */
  value?: number;
  /** Mastery score 0-100. When provided, auto-resolves ring color via getMasteryLabelInfo. */
  score?: number;
  size?: number;
  strokeWidth?: number;
  /** Preset color (used when neither `score` nor `ringColor` is provided). */
  color?: 'indigo' | 'emerald' | 'rose' | 'amber';
  /** Explicit hex color for the ring stroke — overrides both `color` and score-based color. */
  ringColor?: string;
  showLabel?: boolean;
  className?: string;
}
const COLOR_HEX: Record<string, string> = {
  indigo: '#6366F1',
  emerald: '#10B981',
  rose: '#F43F5E',
  amber: '#F59E0B'
};
/**
 * Unified mastery ring component.
 *
 * Supports two usage modes:
 * 1. **Preset color:** `<MasteryRing value={80} color="emerald" />` (parent/teacher portals)
 * 2. **Score-based dynamic color:** `<MasteryRing score={65} />` (learner-facing, resolves via getMasteryLabelInfo)
 *
 * You can also pass `ringColor="#FF0000"` to override any color resolution.
 *
 * Unified as part of D-SCAN-2 D2-3.
 */
export function MasteryRing({
  value,
  score,
  size = 48,
  strokeWidth = 4,
  color = 'indigo',
  ringColor,
  showLabel = true,
  className = ''
}: MasteryRingProps) {
  // Resolve the display value: score takes precedence if value is not provided
  const displayValue = value ?? score ?? 0;
  // Resolve stroke color: explicit ringColor > score-based > preset
  let resolvedColor: string;
  if (ringColor) {
    resolvedColor = ringColor;
  } else if (score !== undefined) {
    resolvedColor = getMasteryLabelInfo(score).ring;
  } else {
    resolvedColor = COLOR_HEX[color] || COLOR_HEX.indigo;
  }
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const progress = Math.min(100, Math.max(0, displayValue));
  const offset = circumference - progress / 100 * circumference;
  return <div className={`relative inline-flex items-center justify-center ${className}`} style={{
    width: size,
    height: size
  }} data-id="element-5409">
      <svg className="transform -rotate-90 w-full h-full" viewBox={`0 0 ${size} ${size}`} data-id="element-5410">
        {/* Background track */}
        <circle stroke="#E2E8F0" strokeWidth={strokeWidth} fill="transparent" r={radius} cx={size / 2} cy={size / 2} data-id="element-5411" />
        {/* Progress ring */}
        <motion.circle stroke={resolvedColor} strokeWidth={strokeWidth} strokeDasharray={circumference} strokeLinecap="round" fill="transparent" r={radius} cx={size / 2} cy={size / 2} initial={{
        strokeDashoffset: circumference
      }} animate={{
        strokeDashoffset: offset
      }} transition={{
        duration: 0.8,
        ease: 'easeOut'
      }} data-id="element-5412" />
      </svg>

      {showLabel && <div className="absolute inset-0 flex items-center justify-center" data-id="element-5413">
          <span className="text-xs font-black text-slate-700" data-id="element-5414">
            {Math.round(displayValue)}
            <span className="text-[10px] text-slate-400" data-id="element-5415">%</span>
          </span>
        </div>}
    </div>;
}